import {
  Coffee,
  DiceFive,
  NavigationArrow,
  PersonSimpleHike,
} from '@phosphor-icons/react'
import { Link, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { KuniriLogo } from '@/components/kuniri-logo'
import { useSessionStore } from '@/lib/auth/session-store'

function SignatureHeadline({ text }: { text: string }) {
  const trimmed = text.trimEnd()
  const last = trimmed.slice(-1)
  const body = last === '.' ? trimmed.slice(0, -1) : trimmed
  return (
    <>
      {body}
      {last === '.' ? <span className="text-coral">.</span> : null}
    </>
  )
}

function OnboardingMap() {
  const { t } = useTranslation()

  return (
    <div className="onboarding-map md:h-[clamp(440px,60vh,620px)]">
      <svg
        className="absolute inset-0 size-full"
        viewBox="0 0 600 720"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
        focusable="false"
      >
        <circle cx="72" cy="108" r="88" fill="#dcefe1" />
        <circle cx="564" cy="590" r="118" fill="#d7ebdd" />
        <path
          d="M-30 250 C 170 130, 330 360, 630 260"
          fill="none"
          stroke="#ffffff"
          strokeWidth="34"
          strokeLinecap="round"
        />
        <path
          d="M90 760 C 130 500, 400 520, 500 -30"
          fill="none"
          stroke="#ffffff"
          strokeWidth="34"
          strokeLinecap="round"
        />
        <path
          d="M-30 250 C 170 130, 330 360, 630 260"
          fill="none"
          stroke="#d3e0dd"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <path
          d="M90 760 C 130 500, 400 520, 500 -30"
          fill="none"
          stroke="#d3e0dd"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <path
          d="M170 230 C 360 300, 400 440, 215 570"
          fill="none"
          stroke="#ff6f59"
          strokeWidth="4"
          strokeLinecap="round"
        />
        <circle cx="450" cy="180" r="25" fill="#00645f" fillOpacity="0.16" />
        <circle cx="120" cy="500" r="25" fill="#00645f" fillOpacity="0.16" />
        <circle cx="320" cy="400" r="25" fill="#00645f" fillOpacity="0.16" />
      </svg>

      <div className="absolute top-5 left-5 inline-flex items-center gap-2 rounded-[20px] bg-white/95 px-3.5 py-3 shadow-card">
        <NavigationArrow
          size={18}
          weight="bold"
          aria-hidden="true"
          className="text-teal"
        />
        <span className="text-[13px] font-bold text-deep">
          {t('screens.onboarding.heroLabel')}
        </span>
      </div>

      <div className="onboarding-map__card top-20 left-3.5">
        <span className="grid size-9 place-items-center rounded-full bg-sole text-deep">
          <Coffee size={18} weight="bold" aria-hidden="true" />
        </span>
        <span className="font-display text-base leading-none font-extrabold text-deep">
          {t('screens.onboarding.hero.aperitivoTitle')}
        </span>
        <span className="text-xs font-bold text-muted">
          {t('screens.onboarding.hero.aperitivoSubtitle')}
        </span>
      </div>

      <div className="onboarding-map__card top-[44%] right-3.5 max-w-[142px]">
        <span className="grid size-8 place-items-center rounded-full bg-teal text-white">
          <PersonSimpleHike size={17} weight="bold" aria-hidden="true" />
        </span>
        <span className="font-display text-[15px] leading-none font-extrabold text-deep">
          {t('screens.onboarding.hero.trekkingTitle')}
        </span>
        <span className="text-xs font-bold text-muted">
          {t('screens.onboarding.hero.trekkingSubtitle')}
        </span>
      </div>

      <div className="onboarding-map__card bottom-14 left-5 max-w-[142px]">
        <span className="grid size-8 place-items-center rounded-full bg-lilla text-white">
          <DiceFive size={17} weight="bold" aria-hidden="true" />
        </span>
        <span className="font-display text-[15px] leading-none font-extrabold text-deep">
          {t('screens.onboarding.hero.giochiTitle')}
        </span>
        <span className="text-xs font-bold text-muted">
          {t('screens.onboarding.hero.giochiSubtitle')}
        </span>
      </div>
    </div>
  )
}

/**
 * Onboarding is the only public branded screen in the foundation. Copy and
 * layout follow UI_PARITY §3.1; no fake people, reviews or location prompt.
 * The logo stays centered on mobile and left-aligned on desktop.
 */
export function OnboardingScreen() {
  const { t } = useTranslation()
  const status = useSessionStore((state) => state.status)

  // Deep links and the onboarding route itself must not show the public
  // landing page to an authenticated user.
  if (status === 'authenticated') return <Navigate to="/home" replace />

  return (
    <main id="main-content" className="onboarding-shell min-h-dvh">
      <div className="mx-auto w-full max-w-[1320px] px-6 pt-[calc(var(--safe-top)+12px)] pb-6 md:px-14 md:py-12">
        <header className="flex justify-center md:justify-start">
          <KuniriLogo
            height={46}
            className="!h-[46px] w-auto md:!h-[54px]"
          />
        </header>

        <div className="mt-5 md:mt-0 md:grid md:grid-cols-[5fr_6fr] md:items-center md:gap-10">
          <section className="text-center md:py-12 md:text-left">
            <h1 className="whitespace-pre-line text-[27px] leading-none font-extrabold tracking-[-0.02em] text-deep md:hidden">
              <SignatureHeadline text={t('screens.onboarding.headlineMobile')} />
            </h1>
            <h1 className="hidden whitespace-pre-line text-[clamp(2.4rem,4vw,3rem)] leading-none font-extrabold tracking-[-0.02em] text-deep md:block">
              <SignatureHeadline text={t('screens.onboarding.headlineDesktop')} />
            </h1>
            <p className="mx-auto mt-3 max-w-md text-[15px] leading-snug text-muted md:hidden">
              {t('screens.onboarding.subtitleMobile')}
            </p>
            <p className="mt-5 hidden max-w-[34rem] text-lg leading-relaxed text-muted md:block">
              {t('screens.onboarding.bodyDesktop')}
            </p>

            <div className="mt-8 hidden md:block">
              <Link
                to="/login"
                className="btn-primary w-[280px] rounded-[18px]"
              >
                {t('screens.onboarding.ctaDesktop')}
              </Link>
              <p className="mt-3.5 text-sm font-bold text-muted">
                {t('screens.onboarding.caption')}
              </p>
            </div>
          </section>

          <div className="mt-7 md:mt-0">
            <OnboardingMap />
          </div>

          <div className="mt-5 md:hidden">
            <Link
              to="/login"
              className="btn-primary min-h-14 w-full rounded-[18px] text-base font-extrabold"
            >
              {t('screens.onboarding.ctaMobile')}
            </Link>
          </div>
        </div>
      </div>
    </main>
  )
}
